/**
 * SUPPRESSION AND REDACTION AS FIRST-CLASS UI, never a blank and never a zero.
 *
 * ══ THE ONE SENTENCE THIS FILE EXISTS FOR ══════════════════════════════════
 *
 * 🔴 `Intl.NumberFormat().format(null)` IS THE STRING "0". So every path that
 * could hand a withheld value to a number formatter has been removed:
 * `formatCell` returns a tagged union, and the three "no number" arms land
 * HERE, on components that render an em-dash plus the REASON. A fabricated zero
 * over a number the system has just decided not to serve is worse than a blank,
 * because a reader cannot tell it from a measured zero — and the operator would
 * act on it.
 *
 * ══ SUPPRESSED, GAP AND REDACTED ARE THREE DIFFERENT FACTS ═════════════════
 *
 *   suppressed  we hold the number and the disclosure floor withheld it at this
 *               grain. The floor is stated; WHICH rule fired is never stated,
 *               because naming the rule would itself leak that the cell is
 *               below k (spec 10.3).
 *   gap         there is no number here.
 *   redacted    your role may not read this metric, or the kill switch is on.
 *               Carries no numeric field of any kind, by construction.
 *
 * Colour is never the only signal: every one of them carries an icon AND text,
 * and every em-dash carries an `aria-label` a screen reader reads instead of
 * the dash.
 */
import { EyeOff, Lock, Minus, ShieldAlert } from 'lucide-react';

import type { FormattedCell } from '../answer-view';
import type { RedactedAnswerCell, WithheldTile } from '../protocol';

/** One table cell. The `value` arm is plain text; React escapes it. */
export function ValueCell({ cell }: { cell: FormattedCell }) {
  if (cell.kind === 'value') return <span className="tabular-nums">{cell.text}</span>;

  /**
   * SUPPRESSED IS NOT MISSING, AND THEY USED TO RENDER IDENTICALLY.
   *
   * Both arms drew the same em-dash behind the same Minus glyph, so the only
   * difference was a hover `title` - invisible on touch, invisible in a
   * screenshot, invisible to anyone reading rather than pointing.
   *
   * "No data" and "withheld to protect a small group" are opposite facts.
   * Reading a suppression as an absence understates a population; reading an
   * absence as a suppression invents one. The glyph draws the WORD, not "<N":
   * a withheld cell can hold a figure above the floor (a row's floor counts its
   * people, not each figure; complementary suppression hides cells at or above
   * it), so "<N" asserted a bound that was sometimes false (PROD, 2026-09-15).
   * The floor itself is public (`k_applied`) and stays in the label. Pinned by
   * tests/unit/lint/agent-withheld-is-not-a-bound.test.ts.
   */
  const isSuppressed = cell.kind === 'suppressed';
  const Glyph = isSuppressed ? EyeOff : Minus;
  return (
    <span
      className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-muted-foreground"
      aria-label={cell.label}
      title={cell.label}
    >
      <Glyph className="h-3 w-3" aria-hidden />
      <span aria-hidden>{isSuppressed ? 'withheld' : cell.text}</span>
    </span>
  );
}

/** The standing notice above a partially-suppressed table (spec 16.1). */
export function SuppressionNotice({ text }: { text: string }) {
  return (
    <div
      className="flex items-start gap-2 rounded-md border bg-muted/60 px-3 py-2 text-xs text-muted-foreground"
      role="note"
    >
      <EyeOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
      <span>{text}</span>
    </div>
  );
}

const REDACTION_COPY: Record<RedactedAnswerCell['reason'], { title: string; body: string }> = {
  permission: {
    title: 'Withheld — you do not hold the key',
    body:
      'This answer re-runs under YOUR permissions, not the sharer’s. The number ' +
      'was not copied into the link and is not being hidden from you locally.',
  },
  metric_unavailable: {
    title: 'Withheld — the metric no longer exists',
    body: 'The catalogue moved or removed this metric id. Nothing was computed.',
  },
  metric_disarmed: {
    title: 'Withheld — this metric is disarmed',
    body:
      'Somebody decided this number is wrong and turned it off. That is the kill ' +
      'switch working: an em-dash here is deliberate, and the wrong-number runbook ' +
      'owns the re-arming.',
  },
  metric_disarm_unavailable: {
    title: 'Withheld — the kill switch could not be read',
    body:
      'Nothing is known about any metric right now, so nothing is served. This is a ' +
      'DIFFERENT fault from a disarmed metric: restore the `agent_marts.metric_disarm` ' +
      'grant.',
  },
};

/**
 * THE EM-DASH CARD. Used by the share page, the pinned board and the dashboard's
 * `withheld[]` array — one component, so the four surfaces spec 8.4 names can
 * never disagree about what a withheld number looks like.
 */
export function RedactedCard({ cell }: { cell: RedactedAnswerCell }) {
  const copy = REDACTION_COPY[cell.reason] ?? {
    title: 'Withheld',
    body: `Reason code: ${cell.reason}`,
  };
  const Icon = cell.reason === 'permission' ? Lock : ShieldAlert;
  return (
    <div className="rounded-lg border bg-muted/40 p-4" role="note">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
        <div className="min-w-0">
          <div className="flex items-baseline gap-2">
            <span
              className="text-3xl font-semibold leading-none text-muted-foreground"
              aria-label={copy.title}
            >
              —
            </span>
            <span className="text-sm font-medium text-foreground">{copy.title}</span>
          </div>
          <p className="mt-1 text-xs text-muted-foreground">{copy.body}</p>
          <dl className="mt-2 space-y-0.5 text-[11px] text-muted-foreground">
            <div className="flex gap-1">
              <dt className="font-medium">Metric</dt>
              <dd className="font-mono">{cell.metric_id}</dd>
            </div>
            {cell.missing_permission_key && (
              <div className="flex gap-1">
                <dt className="font-medium">Missing key</dt>
                <dd className="font-mono">{cell.missing_permission_key}</dd>
              </div>
            )}
          </dl>
        </div>
      </div>
    </div>
  );
}

/**
 * The dashboard twin: a tile the kill switch withheld.
 *
 * `withheld[]` exists because the tile is OMITTED from `tiles[]` rather than
 * sent with null measures — `QueueTile`/`NsmTile` render through
 * `formatNumber(count)`, which would have printed "0". The fact is not lost;
 * it travels in its own array and lands here.
 */
export function WithheldTileCard({ tile }: { tile: WithheldTile }) {
  const copy = REDACTION_COPY[tile.reason];
  return (
    <div className="rounded-lg border border-dashed bg-muted/30 p-4" role="note">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">
        {tile.tile.replace(/_/g, ' ')}
      </div>
      <div className="mt-1 flex items-baseline gap-2">
        <span className="text-3xl font-semibold leading-none text-muted-foreground" aria-label={copy.title}>
          —
        </span>
        <span className="text-sm font-medium text-foreground">{copy.title}</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{copy.body}</p>
      {tile.metric_id && (
        <p className="mt-2 font-mono text-[11px] text-muted-foreground">{tile.metric_id}</p>
      )}
    </div>
  );
}
